import React, { useEffect, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartContainer } from "../view/ChartContainer";
import { CustomTooltip } from "./components/CustomTooltip";
import { transformPieChartData } from "./utils/chartDataTransformers";
import { RISK_COLORS } from "./constants/chartColors";
import * as statisticService from "./statisticService";
import { AuditScope } from "./chartDynamicLabels";
import { useHistory } from "react-router-dom";
import { InteractiveLegend } from "./components/InteractiveLegend";
import DashboardChartSkeleton from "./components/DashboardChartSkeleton";

function RiskScoreChart({ fromDate, toDate, scope = AuditScope.BY_DAY }) {

    const history = useHistory();

    const [rechartsData, setRechartsData] = useState([]);
    const [hiddenSeries, setHiddenSeries] = useState({});
    const [allLegendItems, setAllLegendItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const rsData = await statisticService.getRiskScoreData(scope);
            if (rsData && rsData.labels && rsData.datasets) {
                // Transform data for Recharts and add colors
                const transformed = transformPieChartData(rsData).map(item => ({
                    ...item,
                    color: item.name === "Low" ? RISK_COLORS.low :
                        item.name === "Medium" ? RISK_COLORS.medium :
                            RISK_COLORS.high
                }));
                setRechartsData(transformed);
                // Store all items for legend
                setAllLegendItems(transformed.map(item => ({
                    value: item.name,
                    color: item.color,
                    dataKey: item.name
                })));
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (scope !== AuditScope.CUSTOM)
            fetchData();
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [scope])

    const fetchDataCustom = async () => {
        setIsLoading(true);
        try {
            const rsData = await statisticService.getRiskScoreDataCustom(fromDate, toDate);
            if (rsData && rsData.labels && rsData.datasets) {

                // Transform data for Recharts and add colors
                const transformed = transformPieChartData(rsData).map(item => ({
                    ...item,
                    color: item.name === "Low" ? RISK_COLORS.low :
                        item.name === "Medium" ? RISK_COLORS.medium :
                            RISK_COLORS.high
                }));
                setRechartsData(transformed);
                // Store all items for legend
                setAllLegendItems(transformed.map(item => ({
                    value: item.name,
                    color: item.color,
                    dataKey: item.name
                })));
            }
        } catch (error) {
            console.error('Error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (scope !== AuditScope.CUSTOM) {
            return;
        }

        const fromNotToday = isDateDifferentThanToday(fromDate);
        const toNotToday = isDateDifferentThanToday(toDate);
        checkCustomDatesSelected(fromNotToday, toNotToday);
        /* eslint-disable react-hooks/exhaustive-deps */
    }, [fromDate, toDate, scope])

    const checkCustomDatesSelected = (isFromDate, isToDate) => {
        if (isFromDate === true || isToDate === true) {
            fetchDataCustom();
        }
    }

    const isDateDifferentThanToday = (dateToCheck) => {
        if (!dateToCheck) {
            return false;
        }
        const today = new Date();
        const isSameDay =
            dateToCheck.getFullYear() === today.getFullYear() &&
            dateToCheck.getMonth() === today.getMonth() &&
            dateToCheck.getDate() === today.getDate();

        return !isSameDay;
    };

    // Handle legend click to toggle series visibility
    const handleToggleSeries = (dataKey) => {
        setHiddenSeries(prev => ({
            ...prev,
            [dataKey]: !prev[dataKey]
        }));
    };

    // Calculate timestamp based on scope or custom date range
    const getStartTimestamp = () => {
        const isCustomDate = fromDate && isDateDifferentThanToday(fromDate);

        if (isCustomDate) {
            return fromDate.getTime();
        }

        const now = new Date();
        switch (scope) {
            case AuditScope.BY_WEEK:
                now.setDate(now.getDate() - 7);
                return now.getTime();
            case AuditScope.BY_MONTH:
                now.setMonth(now.getMonth() - 1);
                return now.getTime();
            case AuditScope.BY_DAY:
            default:
                now.setDate(now.getDate() - 1);
                return now.getTime();
        }
    };

    const getEndTimestamp = () => {
        const isCustomDate = toDate && isDateDifferentThanToday(toDate);

        if (isCustomDate) {
            return toDate.getTime();
        }
        return Date.now();
    };

    // Handle chart click to navigate to Recent Violations with filters
    const handlePieClick = (data, index) => {
        if (!data || !data.name) return;

        const selectedSeverity = data.name; // 'Low', 'Medium', or 'High'
        const startTime = getStartTimestamp();
        const endTime = getEndTimestamp();

        // Navigate to Recent Violations with filters
        history.push(`/recentviolations?f_severity=${selectedSeverity}&f_startTime=${startTime}&f_endTime=${endTime}`);
    };

    if (isLoading) {
        return <DashboardChartSkeleton title="Risk Score" subtitle="Violations" isPieChart={true} />;
    }

    return (
        <ChartContainer noHeader={true} className="oct_chart-card-v2">
            <div className="oct_chart-header-v2">
                <p className="oct_chart-subtitle-v2">Violations</p>
                <h3 className="oct_chart-title-v2">Risk Score</h3>
            </div>
            <div className="oct_interactive-legend-wrapper-v2">
                {allLegendItems.length > 0 && (
                    <InteractiveLegend
                        payload={allLegendItems}
                        hiddenSeries={hiddenSeries}
                        onToggleSeries={handleToggleSeries}
                    />
                )}
            </div>
            {(() => {
                const totalValue = rechartsData.reduce((sum, item) => sum + (Number(item.value) || 0), 0);
                const isEmpty = totalValue === 0;

                return (
                    <div className="oct_recharts-wrapper-v2">
                        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 10, height: 10 }}>
                            <PieChart className={isEmpty ? "oct_empty-chart-data" : ""}>
                                <Pie
                                    data={isEmpty ? [{ name: 'No Risk Score', value: 1, color: '#e2e8f0', isEmptyState: true }] : rechartsData.filter(entry => !hiddenSeries[entry.name])}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    paddingAngle={isEmpty ? 0 : 2}
                                    dataKey="value"
                                    onClick={isEmpty ? undefined : handlePieClick}
                                    isAnimationActive={!isEmpty}
                                >
                                    {(isEmpty ? [{ name: 'No Risk Score', value: 1, color: '#e2e8f0', isEmptyState: true }] : rechartsData.filter(entry => !hiddenSeries[entry.name])).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                );
            })()}
        </ChartContainer>

    )
}

export default RiskScoreChart;
